import { useLayoutEffect, useRef, useState, type PointerEvent } from 'react';
import { addDays, currentLocale, dayLabel, weekdayLabel } from '../dates';

const short = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/** "5 Oct – 11 Oct", short enough to sit between the arrows on a phone. */
export const weekRange = (monday: string) => `${short(monday)} – ${short(addDays(monday, 6))}`;

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/**
 * A phone's pager for the week. With `days`, seven days in a row, each one tap, with how many
 * visits it holds and a mark when one needs checking; today keeps its ring whichever day is
 * picked. Without, just the week's first and last dates, as Week view lists the days below it.
 * Either way, swiping sideways moves a week, like the arrows: what is swiped follows the finger,
 * and the new week slides in from the side it is coming from, so a change never looks like the
 * same week redrawn.
 */
export function WeekStrip(props: {
  monday: string;
  today: string;
  title: string;
  stepLabel: string;
  /** The row of days to pick from; left out, the range alone is what is swiped. */
  days?: {
    selected: string | null;
    tally(d: string): { open: number; flagged: number };
    onPick(d: string): void;
  };
  onStep(dir: 1 | -1): void;
  /** Only when now is out of view: there is nowhere to come back to otherwise. */
  onToday?: () => void;
  /** What the way back is called: "This week" from another week, "Today" from another day. */
  nowLabel: string;
}) {
  // A sideways swipe steps a week; the tap that ends a swipe picks nothing.
  const start = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  // How far the row has been dragged, while a finger is on it and moving sideways.
  const [drag, setDrag] = useState<number | null>(null);
  // Which side the week now showing came in from, read off the change of Monday.
  const shown = useRef(props.monday);
  const [enter, setEnter] = useState<'next' | 'prev' | null>(null);
  useLayoutEffect(() => {
    if (shown.current === props.monday) return;
    setEnter(props.monday > shown.current ? 'next' : 'prev');
    shown.current = props.monday;
  }, [props.monday]);
  const release = () => {
    start.current = null;
    setDrag(null);
  };

  /** What makes the swiped element slide: keyed by the week, so a new week animates in. */
  const slide = (base: string) => ({
    key: props.monday,
    className: `${base} slides${drag !== null ? ' dragging' : ''}${enter ? ` enter-${enter}` : ''}`,
    style: drag !== null ? { transform: `translateX(${drag}px)` } : undefined,
    onAnimationEnd: () => setEnter(null),
    onPointerDown: (e: PointerEvent) => {
      start.current = { x: e.clientX, y: e.clientY };
      swiped.current = false;
    },
    onPointerMove: (e: PointerEvent) => {
      const s = start.current;
      if (!s) return;
      const dx = e.clientX - s.x;
      // Follow the finger once it is plainly going sideways, with some give past the swipe.
      if (drag !== null || (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(e.clientY - s.y))) {
        const reach = 64;
        setDrag(Math.abs(dx) <= reach ? dx : Math.sign(dx) * (reach + (Math.abs(dx) - reach) / 3));
      }
    },
    onPointerLeave: () => start.current && release(),
    onPointerUp: (e: PointerEvent) => {
      const s = start.current;
      release();
      if (!s) return;
      const dx = e.clientX - s.x;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(e.clientY - s.y) * 1.5) {
        // Only the click this same gesture ends with is swallowed; the next tap counts.
        swiped.current = true;
        setTimeout(() => (swiped.current = false), 0);
        props.onStep(dx < 0 ? 1 : -1);
      }
    },
    onPointerCancel: release,
  });

  // What letting go will bring, uncovered as the row is pulled aside.
  const hint = drag !== null && drag !== 0 && (
    <span
      className={`strip-hint ${drag < 0 ? 'next' : 'prev'}${Math.abs(drag) > 40 ? ' ready' : ''}`}
      style={{ opacity: Math.min(1, Math.max(0, (Math.abs(drag) - 12) / 28)) }}
      aria-hidden="true"
    >
      {drag < 0 ? 'Next ›' : '‹ Prev'}
    </span>
  );
  const title = <strong aria-live="polite">{props.title}</strong>;
  const pick = props.days;

  return (
    <div className={pick ? 'week-strip' : 'week-strip range-only'}>
      <div className="strip-head">
        <button type="button" className="quiet step" onClick={() => props.onStep(-1)}>
          ‹<span className="sr-only"> Previous {props.stepLabel}</span>
        </button>
        {pick ? (
          title
        ) : (
          <div className="strip-track">
            {hint}
            <div {...slide('strip-range')}>{title}</div>
          </div>
        )}
        {props.onToday && (
          <button type="button" className="quiet now" aria-keyshortcuts="T" onClick={props.onToday}>
            {props.nowLabel}
          </button>
        )}
        <button type="button" className="quiet step" onClick={() => props.onStep(1)}>
          ›<span className="sr-only"> Next {props.stepLabel}</span>
        </button>
      </div>
      {pick && (
        <div className="strip-track">
          {hint}
          <div {...slide('strip-days')} role="group" aria-label="Days this week">
            {Array.from({ length: 7 }, (_, i) => addDays(props.monday, i)).map((d) => {
              const { open, flagged } = pick.tally(d);
              const isToday = d === props.today;
              const on = d === pick.selected;
              return (
                <button
                  key={d}
                  type="button"
                  className={`strip-day${isToday ? ' today' : ''}${on ? ' on' : ''}`}
                  aria-pressed={on}
                  aria-current={isToday ? 'date' : undefined}
                  onClick={() => {
                    if (swiped.current) {
                      swiped.current = false;
                      return;
                    }
                    pick.onPick(d);
                  }}
                >
                  <span className="strip-weekday" aria-hidden="true">
                    {weekdayLabel(d)}
                  </span>
                  <span className="strip-num" aria-hidden="true">
                    {Number(d.slice(8))}
                  </span>
                  <span className="strip-mark" aria-hidden="true">
                    {open > 0 ? open : ''}
                    {flagged > 0 && <span className="strip-flag" />}
                  </span>
                  <span className="sr-only">
                    {dayLabel(d)}
                    {isToday && ', today'}
                    {`, ${open ? plural(open, 'visit') : 'free'}`}
                    {flagged > 0 && `, ${flagged} to check`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
